import React, { useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { DropinTicket } from '../../../../pure-js/types/GrooverTypes'

// School interface for derived school data
interface School {
  clientId: string
  name: string
}

// Helper function to derive unique schools from tickets
const deriveSchoolsFromTickets = (tickets: DropinTicket[]): School[] => {
  const schoolMap = new Map<string, School>()

  tickets.forEach((ticket) => {
    if (!schoolMap.has(ticket.clientId)) {
      schoolMap.set(ticket.clientId, {
        clientId: ticket.clientId,
        name: ticket.schoolName
      })
    }
  })

  return Array.from(schoolMap.values())
}

// Modal Overlay - semi-transparent background
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
`

// Modal Content Container - centered card
const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
  position: relative;

  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f9fafb;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
    height: 100%;
  }
`

// Close Button - X in top-right corner
const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 1;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`

// Modal Inner Content - padding for content
const ModalInnerContent = styled.div`
  padding: 24px;
  padding-top: 48px;
`

// Section Title
const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`

// Section Subtitle
const SectionSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
`

// School List Container
const SchoolList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`

// School Card
const SchoolCard = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 2px solid ${(props) => (props.isSelected ? 'var(--portal-primary-light, #8b5cf6)' : '#e5e7eb')};
  background: ${(props) => (props.isSelected ? 'var(--portal-primary-surface, #f5f3ff)' : '#ffffff')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
  min-height: 44px;

  &:hover {
    border-color: ${(props) => (props.isSelected ? 'var(--portal-primary-light, #8b5cf6)' : '#d1d5db')};
    background: ${(props) => (props.isSelected ? 'var(--portal-primary-surface, #f5f3ff)' : '#f9fafb')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`

// School Name
const SchoolName = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
`

// Selection Indicator
const SelectionIndicator = styled.div<{ isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? 'var(--portal-primary-light, #8b5cf6)' : '#d1d5db')};
  background: ${(props) => (props.isSelected ? 'var(--portal-primary-light, #8b5cf6)' : '#ffffff')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &::after {
    content: '✓';
    color: #ffffff;
    font-size: 14px;
    font-weight: bold;
    opacity: ${(props) => (props.isSelected ? 1 : 0)};
    transition: opacity 0.2s;
  }
`

// Empty State Container
const EmptyStateContainer = styled.div`
  padding: 32px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`

// Continue Button Container
const ContinueButtonContainer = styled.div`
  padding: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 32px;
`

// Continue Link styled as button
const ContinueLink = styled.a<{ disabled: boolean }>`
  display: block;
  width: 100%;
  padding: 16px;
  background: ${(props) => (props.disabled ? '#e5e7eb' : 'var(--portal-primary-light, #8b5cf6)')};
  color: ${(props) => (props.disabled ? '#9ca3af' : '#ffffff')};
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;
  min-height: 44px;
  box-sizing: border-box;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    background: ${(props) => (props.disabled ? '#e5e7eb' : 'var(--portal-primary, #7c3aed)')};
  }

  &:focus {
    outline: none;
    box-shadow: ${(props) => (props.disabled ? 'none' : '0 0 0 3px rgba(139, 92, 246, 0.3)')};
  }
`

// Props Interface
interface BuyDropinTicketsModalProps {
  isOpen: boolean
  onClose: () => void
  tickets: DropinTicket[]
  getText: (key: string) => string
}

// Main Component
export const BuyDropinTicketsModal: React.FC<BuyDropinTicketsModalProps> = ({ isOpen, onClose, tickets, getText }) => {
  // Derive schools from tickets with memoization
  const schools = useMemo(() => deriveSchoolsFromTickets(tickets), [tickets])

  // State for selected school
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null)

  // Ref for focus management
  const modalContentRef = React.useRef<HTMLDivElement>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSchool(null)
    }
  }, [isOpen])

  // Auto-select if only one school when modal opens
  useEffect(() => {
    if (isOpen && schools.length === 1) {
      setSelectedSchool(schools[0])
    }
  }, [isOpen, schools])

  // Handle click on overlay to close modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Handle Escape key to close modal
  React.useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Focus management: trap focus in modal and restore on close
  React.useEffect(() => {
    if (!isOpen) return

    // Store the element that had focus before modal opened
    const previouslyFocusedElement = document.activeElement as HTMLElement

    // Focus the close button when modal opens
    setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 0)

    // Trap focus within modal
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalContentRef.current) return

      const focusableElements = modalContentRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        // Shift + Tab: moving backwards
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        // Tab: moving forwards
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    window.addEventListener('keydown', handleTabKey)

    // Restore focus when modal closes
    return () => {
      window.removeEventListener('keydown', handleTabKey)
      previouslyFocusedElement?.focus()
    }
  }, [isOpen])

  // Keyboard navigation: Enter key to select schools
  const handleSchoolKeyDown = (e: React.KeyboardEvent, school: School) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleSchoolSelect(school)
    }
  }

  // Handle school selection
  const handleSchoolSelect = (school: School) => {
    setSelectedSchool(school)
  }

  // Get checkout URL. Relative to the current origin, so the portal keeps the payer on the host
  // they are already on (app2.coursely.se, a school's own domain) instead of bouncing them to
  // production to pay.
  const checkoutUrl = selectedSchool ? `/checkout/${selectedSchool.clientId}/dropinPackages` : '#'

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <ModalContent ref={modalContentRef} onClick={(e) => e.stopPropagation()}>
        <CloseButton ref={closeButtonRef} type="button" onClick={onClose} aria-label={getText('portalClose')}>
          ×
        </CloseButton>
        <ModalInnerContent>
          {/* School Selection Section */}
          <SectionTitle>{getText('portalSelectSchool')}</SectionTitle>
          <SectionSubtitle>{getText('portalSelectSchoolSubtitle')}</SectionSubtitle>

          {schools.length === 0 ? (
            <EmptyStateContainer>{getText('portalNoSchoolsAvailable')}</EmptyStateContainer>
          ) : (
            <SchoolList>
              {schools.map((school) => (
                <SchoolCard
                  key={school.clientId}
                  isSelected={selectedSchool?.clientId === school.clientId}
                  onClick={() => handleSchoolSelect(school)}
                  onKeyDown={(e) => handleSchoolKeyDown(e, school)}
                  type="button"
                  aria-selected={selectedSchool?.clientId === school.clientId}
                >
                  <SchoolName>{school.name}</SchoolName>
                  <SelectionIndicator isSelected={selectedSchool?.clientId === school.clientId} />
                </SchoolCard>
              ))}
            </SchoolList>
          )}

          {/* Continue to Checkout Link */}
          <ContinueButtonContainer>
            <ContinueLink
              href={checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!selectedSchool}
              onClick={(e) => {
                if (!selectedSchool) {
                  e.preventDefault()
                }
              }}
            >
              {getText('portalContinueToCheckout')}
            </ContinueLink>
          </ContinueButtonContainer>
        </ModalInnerContent>
      </ModalContent>
    </ModalOverlay>
  )
}
